/**
 * WebAuthn service for passkey registration and authentication
 *
 * This service uses the browser's WebAuthn API to create and authenticate with passkeys.
 * The credentials are stored locally by the browser and the public key is sent to the backend.
 */

/**
 * Convert base64url to ArrayBuffer
 */
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer.buffer;
}

/**
 * Convert ArrayBuffer to base64url
 */
function bufferToBase64url(buffer: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(buffer));
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Check if WebAuthn is supported by the browser
 */
export function isWebAuthnSupported(): boolean {
  return window.PublicKeyCredential !== undefined &&
         typeof window.PublicKeyCredential === 'function';
}

/**
 * Check if platform authenticator (Face ID, Touch ID, Windows Hello) is available
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false;
  }

  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Register a new passkey (with backend integration)
 */
export async function registerPasskey(
  _email: string,
  _displayName: string,
  options: any
): Promise<{ credential: PublicKeyCredential; rawId: string; response: any }> {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported by this browser');
  }

  try {
    // Convert base64url strings to ArrayBuffers for the browser API
    const publicKeyOptions = {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      user: {
        ...options.user,
        id: base64urlToBuffer(options.user.id),
      },
    };

    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions as any,
    }) as PublicKeyCredential;

    if (!credential) {
      throw new Error('Failed to create credential');
    }

    const response = credential.response as AuthenticatorAttestationResponse;

    return {
      credential,
      rawId: bufferToBase64url(credential.rawId),
      response: {
        clientDataJSON: bufferToBase64url(response.clientDataJSON),
        attestationObject: bufferToBase64url(response.attestationObject),
      },
    };
  } catch (error: any) {
    if (error.name === 'NotAllowedError') {
      throw new Error('Passkey registration was cancelled');
    } else if (error.name === 'NotSupportedError') {
      throw new Error('Passkey is not supported on this device');
    }
    throw error;
  }
}

/**
 * Authenticate with an existing passkey (with backend integration)
 */
export async function authenticateWithPasskey(
  options: any
): Promise<{ credential: PublicKeyCredential; rawId: string; response: any }> {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported by this browser');
  }

  try {
    // Convert base64url strings to ArrayBuffers for the browser API
    const publicKeyOptions = {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      allowCredentials: options.allowCredentials?.map((cred: any) => ({
        ...cred,
        id: base64urlToBuffer(cred.id),
      })),
    };

    const credential = await navigator.credentials.get({
      publicKey: publicKeyOptions as any,
    }) as PublicKeyCredential;

    if (!credential) {
      throw new Error('Failed to get credential');
    }

    const response = credential.response as AuthenticatorAssertionResponse;

    return {
      credential,
      rawId: bufferToBase64url(credential.rawId),
      response: {
        clientDataJSON: bufferToBase64url(response.clientDataJSON),
        authenticatorData: bufferToBase64url(response.authenticatorData),
        signature: bufferToBase64url(response.signature),
        userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
      },
    };
  } catch (error: any) {
    if (error.name === 'NotAllowedError') {
      throw new Error('Passkey authentication was cancelled');
    } else if (error.name === 'NotSupportedError') {
      throw new Error('Passkey is not supported on this device');
    }
    throw error;
  }
}

/**
 * Store passkey credential ID in localStorage for later use
 */
export function storePasskeyCredential(email: string, credentialId: string): void {
  const stored = localStorage.getItem('passkey_credentials');
  const credentials = stored ? JSON.parse(stored) : {};
  credentials[email] = credentialId;
  localStorage.setItem('passkey_credentials', JSON.stringify(credentials));
}

/**
 * Get stored passkey credential ID for an email
 */
export function getStoredPasskeyCredential(email: string): string | null {
  const stored = localStorage.getItem('passkey_credentials');
  if (!stored) return null;
  const credentials = JSON.parse(stored);
  return credentials[email] || null;
}

/**
 * Remove stored passkey credential
 */
export function removePasskeyCredential(email: string): void {
  const stored = localStorage.getItem('passkey_credentials');
  if (!stored) return;
  const credentials = JSON.parse(stored);
  delete credentials[email];
  localStorage.setItem('passkey_credentials', JSON.stringify(credentials));
}
